/* eslint-disable no-undef */
class ConsentManager extends Core {
	constructor() {
		super();
		this.consentModal = null;
		this.daysToExpiry = 365;
		this.cookieName = "ao_consent";
		this.cookiedomain =
			location.hostname !== "localhost" ? ` domain=.${location.hostname.split(".").splice(1).join(".")};` : "";

		this.consentMode = this.getConsentCookie();
		this.privacyPageUrl = "/pages/privacy-policy/";
		this.cookiePageUrl = "/pages/cookies/";
	}

	init() {
		if (!this.consentMode) {
			this.consentModal = super.addModal("consent-modal", this.handleUserSelection.bind(this));
			const showPrivacybtn = this.consentModal.querySelector("[data-show-privacy-page]");
			const showCookiebtn = this.consentModal.querySelector("[data-show-cookie-page]");
			showPrivacybtn.addEventListener("click", this.showInfo.bind(this, this.privacyPageUrl));
			showCookiebtn.addEventListener("click", this.showInfo.bind(this, this.cookiePageUrl));
		}

		this.loadOmetria();
		this.loadMonsterIn();
		this.loadAmazonTag();
	}

	async showInfo(url, e) {
		e.preventDefault();
		const loading = this.consentModal.querySelector("#loading-privacy");
		loading.classList.remove("hidden");

		const request = await super.asyncRequest({ url, method: "GET" });
		if (request && request.status === 200) {
			const html = request.response;
			const parser = new DOMParser();
			const pageHtml = parser.parseFromString(html, "text/html");
			const pageMain = pageHtml.querySelector("main");
			const pageSection = pageMain.querySelector("section");
			const overlay = document.createElement("div");
			const closeBtn = document.createElement("div");
			closeBtn.innerText = "[Close]";
			closeBtn.setAttribute("data-close-modal", "data-close-modal");
			closeBtn.classList.add("m-12", "text-center", "cursor-pointer", "font-bold", "text-secondary");

			pageSection.classList.add(
				"relative",
				"z-50",
				"w-[940px]",
				"max-w-[95%]",
				"m-[10vh_auto]",
				"overflow-scroll",
				"max-h-[80vh]"
			);
			overlay.classList.add(
				"fixed",
				"inset-0",
				"z-40",
				"bg-black",
				"bg-opacity-70",
				"transition-all",
				"duration-1000",
				"pointer-events-none"
			);

			pageSection.prepend(closeBtn);
			const closeBtn2 = closeBtn.cloneNode(true);
			pageSection.appendChild(closeBtn2);
			pageMain.appendChild(overlay);
			super.addModal(pageMain, this.init.bind(this));
		}
	}

	setConsentCookie(value) {
		const d = new Date();
		d.setTime(d.getTime() + this.daysToExpiry * 24 * 60 * 60 * 1000);
		const cookie = `${this.cookieName}=${encodeURIComponent(value)};${
			this.cookiedomain
		} expires=${d.toUTCString()}; path=/;`;
		document.cookie = cookie;
	}

	getConsentCookie() {
		const decodedCookie = decodeURIComponent(document.cookie);
		const match = decodedCookie.match(new RegExp(`(^| )${this.cookieName}=([^;]+)`));
		return match ? match[2] : null;
	}

	handleUserSelection({ currentTarget }) {
		const chosenOption = currentTarget.getAttribute("id");
		switch (chosenOption) {
			case "accept-all":
				this.acceptAll();
				break;
			case "accept-selection":
				this.acceptSome();
				break;
			default:
				this.rejectAll();
				break;
		}
	}

	setConsent(consent) {
		const consentMode = {
			functionality_storage: consent.necessary ? "granted" : "denied",
			security_storage: consent.necessary ? "granted" : "denied",
			ad_storage: consent.marketing ? "granted" : "denied",
			analytics_storage: consent.analytics ? "granted" : "denied",
			personalization_storage: consent.preferences ? "granted" : "denied",
			ad_user_data: consent.marketing ? "granted" : "denied",
			ad_personalization: consent.marketing ? "granted" : "denied",
		};

		this.consentMode = JSON.stringify(consentMode);

		this.loadOmetria();
		this.loadMonsterIn();
		this.loadAmazonTag();

		// cookie
		this.setConsentCookie(this.consentMode);

		// GTM consent
		gtag("consent", "update", consentMode);
		gtag("event", "consentUpdated");
	}

	acceptAll() {
		this.setConsent({
			necessary: true,
			analytics: true,
			preferences: true,
			marketing: true,
		});
	}

	acceptSome() {
		const consentInputs = Array.from(this.consentModal.querySelectorAll("input"));
		const consent = {};
		consentInputs.forEach((i) => {
			consent[i.name] = i.checked;
		});
		this.setConsent(consent);
	}

	rejectAll() {
		this.setConsent({
			necessary: true,
			analytics: false,
			preferences: false,
			marketing: false,
		});
	}

	loadOmetria() {
		if (!this.consentMode || !ometriaAccountId) return;
		// Load ometria only when user consents
		const { ad_storage: marketing, analytics_storage: analytics } = JSON.parse(this.consentMode);
		if (marketing === "granted" && analytics === "granted") {
			const ometriaSnippet = document.createElement("script");
			ometriaSnippet.setAttribute("src", `https://cdn.ometria.com/tags/${ometriaAccountId}.js`);
			document.body.appendChild(ometriaSnippet);
			ometriaSnippet.onload = () => {
				const ometriaTracker = document.createElement("script");
				document.body.appendChild(ometriaTracker);
				ometriaTracker.setAttribute("src", "/assets/js/ometria-tracker.js");
			};
		}
	}

	loadMonsterIn() {
		if (!this.consentMode || !monsterOptinU || !monsterOptinAC) return;

		const { ad_storage: marketing, analytics_storage: analytics } = JSON.parse(this.consentMode);
		if (marketing === "granted" && analytics === "granted") {
			const s = document.createElement("script");
			s.type = "text/javascript";
			s.src = "https://a.omappapi.com/app/js/api.min.js";
			s.async = true;
			s.dataset.user = monsterOptinU;
			s.dataset.account = monsterOptinAC;
			document.getElementsByTagName("head")[0].appendChild(s);
		}
	}

	loadAmazonTag() {
		if (typeof amazonTagId !== "string" || !this.consentMode) return;
		const { ad_storage: marketing, analytics_storage: analytics } = JSON.parse(this.consentMode);

		if (marketing === "granted" && analytics === "granted") {
			if (window.amzn) return;

			window.amzn = function (...args) {
				window.amzn.q.push([args, Date.now()]);
			};
			window.amzn.q = [];
			window.amzn.version = "0.0";
			const script = document.createElement("script");
			script.src = "https://c.amazon-adsystem.com/aat/amzn.js";
			script.id = "amzn-pixel";
			script.async = true;
			const firstScript = document.getElementsByTagName("script")[0];
			firstScript.parentNode.insertBefore(script, firstScript);

			window.amzn("setRegion", "EU");
			window.amzn("addTag", amazonTagId);
			window.amzn("trackEvent", "PageView");
		}
	}
}

const consentBanner = new ConsentManager();
consentBanner.init();
